<template>
  <slot :title="title">
    <library-content-header class="library-content-header"
                            :title="title"
                            :style="contentHeaderStyle"
                            :content="content">
      <template #navigation>
        <div class="is-flex justify-space-between full-width">
          <div class="is-flex widget-content-header__nav-left">
            <h3 v-if="showTitle"
                class="title-five ml-1">
              {{ title }}
            </h3>
            <bulma-button-icon v-if="showInfoControls && !hasInfoPageOpen"
                               iconClass="fas fa-info-circle links-color"
                               class="pointer-on-hover mr-1 info-btn"
                               :disabled="hasEmptyDetails() || !hasInfoLandingPage"
                               :text="$t('contentViewerMoreInfo')"
                               @click="openInfoPage"/>
            <bulma-button-icon v-if="showInfoControls && canShowBackButton"
                               class="pointer-on-hover mr-1 return-btn"
                               iconClass="fas fa-arrow-left links-color"
                               :text="$t('returnToViewer')"
                               @click="backToContentFromInfoPage"/>
          </div>
          <div class="buttons">
            <widget-quick-add-button v-if="showQuickAdd" />
            <button v-if="hasDownloadableContent"
                    class="button is-hidden-mobile ml-2"
                    @click="customDownload(content.contentUrl)">
              <span class="icon fas fa-download" />
              <span>{{ $t('download') }}</span>
            </button>
            <button v-if="canExport"
                    class="button is-hidden-mobile ml-2"
                    data-unit="export-button"
                    @click="exportContent">
              <span class="icon fas fa-file-export" />
              <span>{{ $t('export') }}</span>
            </button>
            <div v-if="hasDownloadableContent || canExport"
                 class="dropdown is-hidden-tablet is-right"
                 data-unit="actions-dropdown"
                 :class="{'is-active': toggleDropdown}">
              <div class="new-dropdown-trigger">
                <button aria-controls="dropdown-menu"
                        class="button ml-2"
                        @click="toggleDropdown=!toggleDropdown">
                  <span class="icon is-small">
                    <i v-if="toggleDropdown" class="fas fa-times"></i>
                    <i v-else class="fas fa-angle-down"></i>
                  </span>
                </button>
              </div>
              <div class="dropdown-menu"
                   id="dropdown-menu"
                   role="menu"
                   :style="{ 'margin-top': '-0.5rem' }">
                <div class="dropdown-content">
                  <a v-if="hasDownloadableContent"
                     class="dropdown-item">
                    <div class="custom-download"
                         data-unit="download-button"
                         @click="customDownload(content.contentUrl)">
                      <span class="icon fas fa-download" />
                      <span>{{ $t('download') }}</span>
                    </div>
                  </a>
                  <a v-if="canExport"
                     class="dropdown-item"
                     data-unit="export-dropdown-button"
                     @click="exportContent">
                    <span class="icon fas fa-file-export" />
                    <span>{{ $t('export') }}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template #contentActions>
        <!-- Override default content with nothing -->
        <div style="display: none"></div>
      </template>
    </library-content-header>
  </slot>
</template>

<script setup>
import BulmaButtonIcon from '@/components/common/BulmaButtonIcon.vue'
import i18n from '@/locales'
import WidgetQuickAddButton from "@/components/widgets/components/WidgetQuickAddButton.vue"
import LibraryContentHeader from '@/components/library/content/header/LibraryContentHeader'
import { computed, ref, toRaw } from 'vue'
import { customDownload } from '@/helpers/customDownload'
import { useBreakpoints } from '@/helpers/responsiveBreakpoints'
import { useInfoLandingPage } from '@/components/widgets/composables/useInfoLandingPage'
import { useStore } from 'vuex'
import { useWidgetNavigator } from '@/components/widgets/composables/useWidgetNavigator.ts'
import { useWidgetAddToCart } from "@/components/widgets/composables/useWidgetAddToCart"
import { useWidgetExport } from '@/components/widgets/composables/useWidgetExport'
import { WidgetTypes } from '@/interfaces/global/widgets'
import { useFetch } from '@/components/widgets/composables/useFetchContent'

const props = defineProps({
  displayName: {
    type: String,
    default: ''
  },
  hideDownload: {
    type: Boolean,
    default: false
  },
  infoControlsEnabled: {
    type: Boolean,
    required: false,
    default: null
  }
})

const store = useStore()
const { isLoading } = useFetch()
const {
  backToContentFromInfoPage,
  hasInfoLandingPage,
  hasInfoPageOpen,
  openInfoPage,
  previousProductPage,
  productPage
} = useInfoLandingPage()
const { hasWidgetCart } = useWidgetAddToCart()
const { canExport, exportContent } = useWidgetExport()
const { isBook, page } = useWidgetNavigator()
const { outerWidth } = useBreakpoints()

const canShowBackButton = computed(() => {
  if (!hasInfoPageOpen.value) {
    return false
  }
  if (type.value === "product-page") {
    return false
  }
  if (type.value === "part") {
    return (isBook.value) || (page.value !== null) || (previousProductPage.value !== null)
  }
  return true
})

const content = computed(() => store.state.content)

const showToc = computed(() => {
  return store.getters['widgets/showToc']
})

const hasTocData = computed(() => {
  return Boolean(content.value?.toc?.book) && ((content.value?.toc?.items?.length ?? 0) > 0)
})

const showInfoControls = computed(() => {
  // Show info controls for non-book widgets (page, product-page) if info landing page is enabled
  if (!isBook.value || productPage.value !== null) {
    return hasInfoLandingPage.value
  }
  if (props.infoControlsEnabled !== null) {
    return props.infoControlsEnabled
  }
  if (isLoading.value) return false
  return showToc.value && isBook.value && hasTocData.value
})

const hasDownloadableContent = computed(() => {
  if (props.hideDownload) return false

  const isBookPrintingDisabled = (store.state.content?.disableExport ?? false) ||
    (store.state.content?.toc?.book?.disableExport ?? false)

  if (['document', 'image', 'video'].includes(store.state.content.contentType)) {
    return !isBookPrintingDisabled
  }

  return false
})

const toggleDropdown = ref(false)
const type = computed(() => (content.value.type === 'media')
  ? content.value.contentType
  : content.value.type)

const title = computed(() => {
  if (props.displayName) return props.displayName

  return content.value?.isLoaded
    ? content.value.partNumber || content.value.name
    : i18n.global.tc(type.value, 1)
})

const contentHeaderStyle = computed(() => {
  const justify = hasInfoPageOpen.value ? 'space-between' : 'flex-start'
  return `justify-content: ${justify}; ` +
    'padding: 0.5rem; ' +
    'max-height: 13%;'
})
// computed property isn't calculating proper value. Converting this to function does the trick
const hasEmptyDetails = () => {
  const contentRaw = toRaw(content.value)
  return contentRaw.type !== 'page'
    ? !contentRaw.tags?.total
      && !contentRaw.tagCount
      && !contentRaw.comments?.total
      && !contentRaw.related?.total
      && !contentRaw.attachments?.total
      && !contentRaw.whereused?.total
    : false
}
// there is a difference in breakpoint returned for widgets vs normal ui for window.innerWidth
// in the browser emulator, the iframe outerWidth is the normal ui equivalent of window.innerWidth
// widgets should be using this outerWidth as the media breakpoint
const isMobile = computed(() => {
  return outerWidth.value <= 768
})
const widgetType = computed(() => {
  return store.getters['widgets/widgetType']
})
const showTitle = computed(() => {
  return (widgetType.value === WidgetTypes.page)
    || !isMobile.value
})

const showQuickAdd = computed(() => {
  return hasWidgetCart.value && content.value.enableQuickAddForContent && (widgetType.value === WidgetTypes.library)
})
</script>

<style lang="scss" scoped>
.dropdown-item:hover {
  background-color: hsl(0, 0%, 98%);
  color: #3E7ACC;
}

.widget-content-header__nav-left {
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.widget-content-header__nav-left .title-five {
  min-width: 0;
}

.widget-content-header__nav-left :deep(.info-btn),
.widget-content-header__nav-left :deep(.return-btn) {
  flex: 0 0 auto;
}
</style>
